import React from "react";
import PropTypes from "prop-types";
import ChatPane from "../ChatPane/ChatPane";

// Stable module-level no-op so ChatPane's onClose-dependent effects
// don't see a fresh function reference on every render. Close lives
// on the AI panel header under the new tabbed chrome — ChatPane's
// internal "close-on-stale-state" check becomes a tab-level no-op.
const NOOP = () => {};

/**
 * Wrapper that mounts the existing matter-scoped `ChatPane` underneath the
 * shared AI panel chrome.
 *
 * `ChatPane` was previously the entire right-rail panel (with its own
 * header + close button) — under the AI panel it becomes one of three
 * tabs and the panel's own header owns the close affordance. We pass a
 * no-op `onClose` so any internal "close-on-stale-state" check inside
 * `ChatPane` becomes a tab-level no-op rather than a global pane close.
 *
 * `setActiveTabRef` and `clearFactSelectionRef` flow straight through so
 * existing in-document fact-citation behavior keeps working unchanged.
 *
 * Note: AIPane wraps every tab body (including this one) in an
 * `AIPaneTabContent` via its `tabItems` memo, so we don't add another
 * one here — a second wrapper double-nests two flex columns and breaks
 * ChatPane's height calc.
 */
const ChatTab = ({ clearFactSelectionRef, setActiveTabRef, documentId }) => {
  return (
    <ChatPane
      onClose={NOOP}
      clearFactSelectionRef={clearFactSelectionRef}
      setActiveTabRef={setActiveTabRef}
      documentId={documentId}
    />
  );
};

ChatTab.propTypes = {
  clearFactSelectionRef: PropTypes.shape({
    current: PropTypes.func,
  }),
  setActiveTabRef: PropTypes.shape({
    current: PropTypes.func,
  }),
  // Active document in the viewer surface — enables document-scoped
  // chat (readiness gate + "This document / Whole matter" toggle).
  documentId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

ChatTab.defaultProps = {
  clearFactSelectionRef: undefined,
  setActiveTabRef: undefined,
  documentId: null,
};

export default ChatTab;
